import { useCallback, useEffect, useState } from "react";

import {
	getQuoteTemplates,
	createQuoteTemplate,
	updateQuoteTemplate,
	deleteQuoteTemplate,
} from "../../../../api";
import type {
	QuoteTemplate,
	SaveQuoteTemplatePayload,
} from "../../../../api/quote-templates-api";

/**
 * Owns the list-level CRUD state for quote templates: the fetched collection,
 * the loading/saving/deleting flags, and the network calls themselves.
 *
 * The hook intentionally does NOT know about the draft editor — `saveTemplate`
 * accepts a pre-built payload and an optional `existingId` so the caller can
 * decide whether it's a create or an update.
 */
export function useQuoteTemplates() {
	const [templates, setTemplates] = useState<QuoteTemplate[]>([]);
	const [isLoading, setIsLoading] = useState(true);
	const [isSaving, setIsSaving] = useState(false);
	const [isDeleting, setIsDeleting] = useState(false);

	const fetchTemplates = useCallback(async () => {
		setIsLoading(true);
		try {
			const data = await getQuoteTemplates();
			setTemplates(data ?? []);
		} catch {
			// Error handled by global interceptor
		} finally {
			setIsLoading(false);
		}
	}, []);

	useEffect(() => {
		fetchTemplates();
	}, [fetchTemplates]);

	const saveTemplate = useCallback(
		async (
			payload: SaveQuoteTemplatePayload,
			existingId?: number
		): Promise<boolean> => {
			setIsSaving(true);
			try {
				if (existingId != null) {
					await updateQuoteTemplate(existingId, payload);
				} else {
					await createQuoteTemplate(payload);
				}
				await fetchTemplates();
				return true;
			} catch {
				// Error handled by global interceptor
				return false;
			} finally {
				setIsSaving(false);
			}
		},
		[fetchTemplates]
	);

	const removeTemplate = useCallback(
		async (id: number): Promise<boolean> => {
			setIsDeleting(true);
			try {
				await deleteQuoteTemplate(id);
				await fetchTemplates();
				return true;
			} catch {
				return false;
			} finally {
				setIsDeleting(false);
			}
		},
		[fetchTemplates]
	);

	return {
		templates,
		isLoading,
		isSaving,
		isDeleting,
		fetchTemplates,
		saveTemplate,
		removeTemplate,
	};
}
